import { useEffect, useState } from 'react'
import { Loader2, RefreshCw, ShieldAlert } from 'lucide-react'
import { algorithmicScannerService } from '@/services/algorithmicScanner'

function pct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return `${(Number(v) * 100).toFixed(1)}%`
}

function r(v, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  const n = Number(v)
  return `${n >= 0 ? '+' : ''}${n.toFixed(digits)}R`
}

const INDICATOR_LABELS = {
  ict: 'ICT / SMC',
  showi_ict: 'SHOWI ICT',
  squeeze_breakout: 'Squeeze Breakout',
  smart_breakout: 'Smart Breakout',
  breakout_pattern: 'Patrón de Ruptura',
  canales_sr: 'Canales S/R',
  liquidity_sweep_pro: 'Liquidity Sweep Pro',
  supertrend_quantum: 'SuperTrend Quantum',
}

function scopeLabel(row) {
  const parts = []
  if (row.indicator_id) parts.push(INDICATOR_LABELS[row.indicator_id] || row.indicator_id)
  if (row.symbol) parts.push(row.symbol)
  if (row.timeframe) parts.push(row.timeframe)
  return parts.join(' · ') || 'Todas las señales'
}

const CONFIDENCE_STYLE = {
  alta: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  media: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  baja: 'bg-slate-200 dark:bg-gray-800 text-slate-600 dark:text-gray-400',
}

const GROUP_BY_OPTIONS = [
  { value: 'indicator', label: 'Indicador' },
  { value: 'asset', label: 'Activo' },
  { value: 'timeframe', label: 'Timeframe' },
  { value: 'combo', label: 'Indicador × Activo × TF' },
]

const MIN_SIGNALS_OPTIONS = [15, 20, 30, 50]

function MultiplierBlock({ title, stats, highlight = false }) {
  return (
    <div className={`rounded-lg p-3 ${highlight ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-50 dark:bg-gray-800'}`}>
      <p className="text-[10px] uppercase text-slate-500 dark:text-gray-400">{title}</p>
      <p className={`text-lg font-semibold ${stats.total_r >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
        {r(stats.total_r)}
      </p>
      <p className="text-[11px] text-slate-500 dark:text-gray-400">
        {stats.sample_size} señales · {pct(stats.win_rate)} win rate · PF {stats.profit_factor !== null && stats.profit_factor !== undefined ? stats.profit_factor.toFixed(2) : '—'}
      </p>
    </div>
  )
}

function RecommendationCard({ row }) {
  const changed = row.best_multiplier !== row.baseline_multiplier
  return (
    <div className={`card space-y-3 ${changed ? 'border-l-4 border-l-emerald-500' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-semibold text-slate-900 dark:text-white">{scopeLabel(row)}</h4>
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${CONFIDENCE_STYLE[row.confidence] || CONFIDENCE_STYLE.baja}`}>
          confianza {row.confidence}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <MultiplierBlock title={`SL actual (${row.baseline_multiplier}x)`} stats={row.baseline} />
        <MultiplierBlock
          title={changed ? `SL recomendado (${row.best_multiplier}x)` : `Mejor probado (${row.best_multiplier}x)`}
          stats={row.best}
          highlight={changed}
        />
      </div>

      {changed && (
        <div className="flex items-center gap-4 text-xs">
          <span className="text-emerald-600 dark:text-emerald-400 font-medium">
            Mejora: {r(row.improvement_r)}
          </span>
          <span className="text-slate-500 dark:text-gray-400">
            {row.losses_flipped} pérdida(s) se habrían convertido en ganancia o empate
          </span>
        </div>
      )}

      <p className="text-xs text-slate-500 dark:text-gray-400">{row.recommendation}</p>
    </div>
  )
}

export default function SlRecommendations({ scannerId = null }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [groupBy, setGroupBy] = useState('indicator')
  const [minSignals, setMinSignals] = useState(15)

  useEffect(() => {
    load(groupBy, minSignals)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannerId, groupBy, minSignals])

  const load = async (gb, min) => {
    setLoading(true)
    setError(null)
    try {
      const { data: res } = scannerId
        ? await algorithmicScannerService.getScannerSlRecommendations(scannerId, gb, min)
        : await algorithmicScannerService.getGlobalSlRecommendations(gb, min)
      setData(res)
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudieron cargar las recomendaciones de SL')
    } finally {
      setLoading(false)
    }
  }

  const items = data?.items || []
  const actionable = items.filter((row) => row.best_multiplier !== row.baseline_multiplier)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="section-title flex items-center gap-2">
          <ShieldAlert size={16} />
          Recomendaciones de ajuste de SL
        </h3>
        <div className="flex items-center gap-3">
          <label className="text-[10px] text-slate-500 dark:text-gray-400 flex items-center gap-1.5">
            Agrupar por
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="text-xs bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded px-1.5 py-0.5"
            >
              {GROUP_BY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="text-[10px] text-slate-500 dark:text-gray-400 flex items-center gap-1.5">
            Mín. señales
            <select
              value={minSignals}
              onChange={(e) => setMinSignals(Number(e.target.value))}
              className="text-xs bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded px-1.5 py-0.5"
            >
              {MIN_SIGNALS_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <button
            onClick={() => load(groupBy, minSignals)}
            disabled={loading}
            className="text-[10px] text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200 flex items-center gap-1"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refrescar
          </button>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 dark:text-gray-400">
        Cada recomendación se mide re-simulando las señales ya resueltas con el SL escalado ×1.25/×1.5/×2/×3
        sobre las mismas velas históricas — no es una estimación estadística indirecta. Solo se muestran grupos
        con al menos {minSignals} señales resueltas de evidencia.
      </p>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-slate-400" size={24} />
        </div>
      ) : error ? (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-red-500/10 text-red-700 dark:text-red-300 text-sm border border-red-500/30">
          <span>{error}</span>
          <button onClick={() => load(groupBy, minSignals)} className="text-xs underline">Reintentar</button>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-slate-500 dark:text-gray-400 text-sm">
          <p>Todavía no hay suficientes señales resueltas para analizar el SL con confianza.</p>
          <p className="text-xs mt-1">Baja el umbral de "Mín. señales" o deja correr el escáner más tiempo.</p>
        </div>
      ) : (
        <>
          {actionable.length === 0 && (
            <div className="text-sm text-slate-500 dark:text-gray-400 py-2">
              El SL actual ya es el que mejor rendimiento ha dado en todos los grupos analizados — no hay
              cambios que recomendar por ahora.
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {items.map((row) => (
              <RecommendationCard key={`${row.indicator_id}-${row.symbol}-${row.timeframe}`} row={row} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
